import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

// Fixed sample clock of the lock screen (not the real time: it is a mockup).
export const SAMPLE_LOCK_SCREEN_TIME = DateTime.fromObject({
  year: 2026,
  month: 11,
  day: 7,
  hour: 9,
  minute: 41,
});

/** First arrival, once the column has painted. */
export const STUDIO_ENTRANCE_DELAY_MS = 500;
/** How long a notification takes to fade where it is (on launch). */
export const STUDIO_EXIT_MS = 200;

// Room left around the device inside the column (400px wide: the ~316px
// device keeps its natural width).
const STAGE_PADDING_PX = 32;

/**
 * Bigger than the 300px mockup when the column has room: closer to a real
 * iPhone's size, so the notification text reads as it will on the phone.
 */
export const STUDIO_MAX_DEVICE_SCALE = 1.25;

/** Scale (≤ `max`) that fits the device in the stage, minus the padding. */
export function fitDeviceScale({
  stageWidth,
  stageHeight,
  deviceWidth,
  deviceHeight,
  padding = STAGE_PADDING_PX,
  max = 1,
}: {
  stageWidth: number;
  stageHeight: number;
  deviceWidth: number;
  deviceHeight: number;
  padding?: number;
  max?: number;
}): number {
  if (deviceWidth <= 0 || deviceHeight <= 0) return 1;
  return Math.max(
    0,
    Math.min(
      max,
      (stageWidth - padding * 2) / deviceWidth,
      (stageHeight - padding * 2) / deviceHeight
    )
  );
}

/**
 * Creating a campaign (CampaignComposerPresenter + KorzaStudioPreview, in
 * step): the main button turns into a check while the phone goes to its
 * always-on display (no notification) and the clock rolls to the launch
 * time; the form gives way to the confirmation; once the clock has landed,
 * the screen wakes and the push rises as customers will get it.
 */
export const CONFIRM_BUTTON_CHECK_MS = 650;
export const CONFIRM_CLOCK_ROLL_MS = 1100;
export const CONFIRM_ARRIVAL_MS = 1600;

/** Lock screen clock: "19:00" (24h locales) or "7:00" (en, no AM/PM). */
export function formatLockScreenClock(at: DateTime, locale: string): string {
  return at.toFormat(locale.startsWith('en') ? 'h:mm' : 'H:mm');
}

/** Lock screen date line, as the Studio shows the sample one. */
export function formatLockScreenDate(at: DateTime, locale: string): string {
  return at.setLocale(locale).toLocaleString({
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** `false`, then `true` once `active` has held for `delayMs` (or at once). */
export function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!active) {
      setOn(false);
      return;
    }
    if (delayMs <= 0) {
      setOn(true);
      return;
    }
    const timeout = setTimeout(() => setOn(true), delayMs);
    return () => clearTimeout(timeout);
  }, [active, delayMs]);
  return on;
}
